// The feed doesn't expose <itunes:season>, so the title is the source of truth
// for season grouping.

const DEFAULT_FEED_URL = "https://feed.podbean.com/therapiefertility/feed.xml";
const FEED_REVALIDATE_SECONDS = 60 * 60; // 1h — new episodes land within an hour.

export type PodcastEpisode = {
  id: string;
  /** Season/episode prefix stripped from the raw title. */
  title: string;
  rawTitle: string;
  /** Season number parsed from the title. `null` for specials. */
  season: number | null;
  /** Per-season episode number parsed from the title. `null` if absent. */
  number: number | null;
  /** Global iTunes episode counter (1..N across the whole feed). */
  globalNumber: number | null;
  pubDate: string;
  formattedDate: string;
  /** Duration in seconds. */
  duration: number;
  audioUrl: string;
  pageUrl: string;
  description: string;
  special: boolean;
};

export type PodcastSeason = {
  number: number;
  episodes: PodcastEpisode[];
};

export async function fetchPodcastEpisodes(
  url: string = DEFAULT_FEED_URL,
): Promise<PodcastEpisode[]> {
  try {
    const res = await fetch(url, {
      next: { revalidate: FEED_REVALIDATE_SECONDS, tags: ["podcast-feed"] },
    });
    if (!res.ok) return [];
    const xml = await res.text();
    return parseFeed(xml);
  } catch {
    return [];
  }
}

export function groupBySeason(episodes: PodcastEpisode[]): PodcastSeason[] {
  const map = new Map<number, PodcastEpisode[]>();
  for (const ep of episodes) {
    if (ep.season == null) continue;
    const arr = map.get(ep.season) ?? [];
    arr.push(ep);
    map.set(ep.season, arr);
  }
  const seasons: PodcastSeason[] = [];
  for (const [number, eps] of map.entries()) {
    eps.sort((a, b) => {
      const an = a.number ?? -1;
      const bn = b.number ?? -1;
      return an - bn;
    });
    seasons.push({ number, episodes: eps });
  }
  seasons.sort((a, b) => a.number - b.number);
  return seasons;
}

export function findLatestEpisode(
  episodes: PodcastEpisode[],
): PodcastEpisode | null {
  if (episodes.length === 0) return null;
  // Feed lists newest item first — the first item with a real audio URL wins.
  return episodes.find((e) => Boolean(e.audioUrl)) ?? null;
}

export function formatDuration(seconds: number): string {
  if (!seconds || seconds < 0) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function parseFeed(xml: string): PodcastEpisode[] {
  const items = xml.match(/<item>[\s\S]*?<\/item>/g) || [];
  const out: PodcastEpisode[] = [];
  for (const item of items) {
    const ep = parseItem(item);
    if (ep) out.push(ep);
  }
  return out;
}

function parseItem(item: string): PodcastEpisode | null {
  const rawTitle = textOf(item.match(/<title>([\s\S]*?)<\/title>/));
  if (!rawTitle) return null;
  const audioUrl = item.match(/<enclosure[^>]*url="([^"]+)"/)?.[1] ?? "";
  const id =
    textOf(item.match(/<guid[^>]*>([\s\S]*?)<\/guid>/)) ||
    audioUrl ||
    rawTitle;
  const pubRaw = textOf(item.match(/<pubDate>([\s\S]*?)<\/pubDate>/));
  const pubDate = pubRaw ? new Date(pubRaw).toISOString() : "";
  const formattedDate = pubRaw ? formatDate(pubRaw) : "";
  const duration = Number(
    textOf(item.match(/<itunes:duration>([\s\S]*?)<\/itunes:duration>/)) || 0,
  );
  const pageUrl = textOf(item.match(/<link>([\s\S]*?)<\/link>/));
  const description = cleanDescription(
    stripHtml(
      cdata(
        textOf(item.match(/<description>([\s\S]*?)<\/description>/)) ||
          textOf(
            item.match(/<itunes:summary>([\s\S]*?)<\/itunes:summary>/),
          ),
      ),
    ),
  );
  const globalRaw = textOf(
    item.match(/<itunes:episode>([\s\S]*?)<\/itunes:episode>/),
  );
  const globalNumber = globalRaw ? Number(globalRaw) : null;

  const meta = parseTitleMeta(rawTitle);
  return {
    id,
    rawTitle,
    title: meta.title,
    season: meta.season,
    number: meta.number,
    globalNumber,
    pubDate,
    formattedDate,
    duration,
    audioUrl,
    pageUrl,
    description,
    special: meta.special,
  };
}

// Title formats found in the wild:
//   "Season 4| Episode 7| Inside the Fertility Clinic..."
//   "Season 4 | Episode 3 | Hannah & Cian's Journey"
//   "Fertility Talks | Season 4 Ep 1 | Dr. John Kennedy"
//   "Fertility Talks | Season2 Ep 9 | Prof. Sheena Lewis"
//   "Fertility Talks | Episode 10 | Bonus Minisode"     ← S1 implied
//   "Fertility Talks | Episode One | Thérapie Fertility" ← S1 implied
//   "Fertility Talks | Special | Louise Brown"          ← no season
function parseTitleMeta(raw: string): {
  season: number | null;
  number: number | null;
  title: string;
  special: boolean;
} {
  const cleaned = raw.replace(/\s+/g, " ").trim();

  // "Season N | Episode N | Title" or "Season N| Ep N| Title"
  let m = cleaned.match(
    /^Season\s*(\d+)\s*\|\s*(?:Episode|Ep)\s*(\d+|[A-Za-z]+)\s*\|\s*(.+)$/i,
  );
  if (m) {
    return {
      season: Number(m[1]),
      number: episodeNum(m[2]),
      title: m[3].trim(),
      special: false,
    };
  }

  // "Fertility Talks | Season N Ep N | Title"
  m = cleaned.match(
    /^Fertility Talks\s*\|\s*Season\s*(\d+)\s*(?:Ep|Episode)\s*(\d+|[A-Za-z]+)\s*\|\s*(.+)$/i,
  );
  if (m) {
    return {
      season: Number(m[1]),
      number: episodeNum(m[2]),
      title: m[3].trim(),
      special: false,
    };
  }

  // "Fertility Talks | Episode N | Title"  → S1 implied
  m = cleaned.match(
    /^Fertility Talks\s*\|\s*Episode\s*(\d+|[A-Za-z]+)\s*\|\s*(.+)$/i,
  );
  if (m) {
    return {
      season: 1,
      number: episodeNum(m[1]),
      title: m[2].trim(),
      special: false,
    };
  }

  // "Fertility Talks | Special | Title"
  m = cleaned.match(/^Fertility Talks\s*\|\s*Special\s*\|\s*(.+)$/i);
  if (m) {
    return { season: null, number: null, title: m[1].trim(), special: true };
  }

  return { season: null, number: null, title: cleaned, special: false };
}

const WORDS_TO_NUM: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
};

function episodeNum(token: string): number | null {
  const n = Number(token);
  if (Number.isFinite(n)) return n;
  return WORDS_TO_NUM[token.toLowerCase()] ?? null;
}

// Podbean feeds escape `&` in titles as `&amp;`, so decode entities anywhere we
// surface a feed string.
function textOf(match: RegExpMatchArray | null): string {
  return decodeEntities(match?.[1]?.trim() ?? "");
}

function cdata(s: string): string {
  return s.replace(/^<!\[CDATA\[/, "").replace(/\]\]>$/, "").trim();
}

function decodeEntities(s: string): string {
  return s
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/&#x([0-9a-f]+);/gi, (_, n) =>
      String.fromCharCode(parseInt(n, 16)),
    );
}

function stripHtml(s: string): string {
  return decodeEntities(
    s
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>\s*<p>/gi, "\n\n")
      .replace(/<[^>]+>/g, "")
      .replace(/\n{3,}/g, "\n\n"),
  ).trim();
}

function cleanDescription(text: string): string {
  return text
    .replace(/if you enjoy(?:ed)? this episode[^\n]*subscribe[^\n]*/gi, "")
    .replace(/\bwww\.therapiefertility\.com\b/gi, "")
    .split("\n")
    .map((line) => line.trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function formatDate(raw: string): string {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-IE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
